import React from 'react'
import type { Block } from '../../types'
import { PropertyLabel, PropertyInput } from './shared'

interface ComparePropertiesProps {
  block: Block
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

export default function CompareProperties({ block, onUpdateBlock }: ComparePropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  const showLabels = config.showLabels !== false
  const orientation = (config.orientation as string) || 'horizontal'
  const initialPosition = (config.initialPosition as number) ?? 50

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Before Image URL</PropertyLabel>
        <PropertyInput
          value={(config.beforeUrl as string) ?? ''}
          onChange={(e) => updateConfig({ beforeUrl: e.target.value })}
          placeholder="https://... (left / top)"
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>After Image URL</PropertyLabel>
        <PropertyInput
          value={(config.afterUrl as string) ?? ''}
          onChange={(e) => updateConfig({ afterUrl: e.target.value })}
          placeholder="https://... (right / bottom)"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <PropertyLabel>Before Label</PropertyLabel>
          <PropertyInput
            value={(config.beforeLabel as string) ?? 'Before'}
            onChange={(e) => updateConfig({ beforeLabel: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <PropertyLabel>After Label</PropertyLabel>
          <PropertyInput
            value={(config.afterLabel as string) ?? 'After'}
            onChange={(e) => updateConfig({ afterLabel: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Orientation</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {(['horizontal', 'vertical'] as const).map((dir) => (
            <button
              key={dir}
              onClick={() => updateConfig({ orientation: dir })}
              className={`py-2 px-3 rounded-lg border-2 text-xs font-medium capitalize transition-all ${
                orientation === dir
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {dir}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Aspect Ratio</PropertyLabel>
        <div className="grid grid-cols-3 gap-1.5">
          {([
            { value: '16/10', label: 'Landscape' },
            { value: '1/1', label: 'Square' },
            { value: '9/16', label: 'Portrait' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateConfig({ aspectRatio: opt.value })}
              className={`py-2 px-2 rounded-lg border-2 text-[10px] font-bold uppercase tracking-wider transition-all ${
                ((config.aspectRatio as string) || '16/10') === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-400 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <PropertyLabel>Start Position</PropertyLabel>
          <span className="text-xs font-mono text-slate-400">{initialPosition}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={initialPosition}
          onChange={(e) => updateConfig({ initialPosition: Number(e.target.value) })}
          className="w-full accent-[#5048e5]"
        />
      </div>

      <div className="flex items-center justify-between">
        <PropertyLabel>Show Labels</PropertyLabel>
        <button
          onClick={() => updateConfig({ showLabels: !showLabels })}
          className={`relative w-10 h-5 rounded-full transition-colors ${showLabels ? 'bg-[#5048e5]' : 'bg-slate-300'}`}
        >
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showLabels ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>
    </div>
  )
}
